(() => {
  'use strict';

  const RUNTIME_KEY = 'U20MobileScrollRecovery';
  if (window[RUNTIME_KEY]?.recover) return;

  const MOBILE_QUERY = window.matchMedia('(max-width: 749px)');
  const root = document.documentElement;
  const retryTimers = new Set();

  const OPEN_OWNER_SELECTOR = [
    '.drawer.is-open',
    'header-search-popdown details[open]',
    '.modal__overlay--password.is-open',
    '.product-quick-add.is-open',
    '[data-u20-mobile-drawer-cloud]:not([hidden])'
  ].join(',');

  const INTERACTION_CLASSES = [
    'u20-vending-tunnel-leaving',
    'u20-vending-tunnel-arrival',
    'u20-vending-tunnel-canvas-ready',
    'u20-vending-tunnel-canvas-fallback'
  ];

  function pageHasOpenScrollOwner() {
    if (document.querySelector(OPEN_OWNER_SELECTOR)) return true;
    return Array.from(document.querySelectorAll('[role="dialog"][aria-modal="true"]'))
      .some(dialog => {
        /* Shopify keeps closed dialog templates in the live DOM. Some of them
           retain layout boxes even while a closed <details> or an aria-hidden
           ancestor makes them non-interactive. getClientRects() alone therefore
           produced a false owner and skipped the panorama unlock entirely. */
        if (dialog.hidden || dialog.getAttribute('aria-hidden') === 'true') return false;
        if (dialog.closest('[hidden], [aria-hidden="true"], details:not([open])')) return false;
        const style = window.getComputedStyle(dialog);
        return style.display !== 'none' &&
          style.visibility !== 'hidden' &&
          style.pointerEvents !== 'none' &&
          dialog.getClientRects().length > 0;
      });
  }

  function hasThemeScrollLock() {
    return root.hasAttribute('data-scroll-locked') ||
      document.body.style.position === 'fixed' ||
      document.body.style.overflow === 'hidden';
  }

  function releaseTransitionOwners() {
    try { window.U20VendingTunnel?.release?.(); } catch (_) {}

    root.classList.remove(...INTERACTION_CLASSES);
    root.style.removeProperty('--u20-vending-tunnel-aperture-scale');

    const tunnelOverlay = document.querySelector('[data-u20-vending-tunnel-overlay]');
    if (tunnelOverlay) tunnelOverlay.setAttribute('aria-hidden', 'true');
  }

  function requestThemeUnlock(forceThemeUnlock) {
    const hadVisibleLock = hasThemeScrollLock();
    if (!hadVisibleLock && !forceThemeUnlock) return false;

    /* The theme's first unlock is intentionally ignored while this marker is
       present. Removing a stale marker first guarantees that this dispatch
       reaches BodyScrollLock.clearAllBodyScrollLocks() on iOS. */
    document.body.removeAttribute('data-drawer-closing');

    const currentY = window.scrollY || window.pageYOffset || 0;
    document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));

    /* Do not rely exclusively on the theme event. Its unlock function owns a
       two-call drawer-closing guard, while iOS BodyScrollLock owns the actual
       document touchmove listener inside a closure. At these verified safe
       panorama endpoints, clear that listener directly as well. */
    try { window.themeVendor?.BodyScrollLock?.clearAllBodyScrollLocks?.(); } catch (_) {}
    root.removeAttribute('data-scroll-locked');
    document.body.removeAttribute('data-drawer-closing');

    /* A forced cleanup can run after an invisible iOS touch listener survived
       while all visible lock styles were already gone. In that narrow case,
       preserve the current page position instead of accepting an old drawer's
       stored scroll position. */
    if (!hadVisibleLock && forceThemeUnlock) {
      window.requestAnimationFrame(() => window.scrollTo(0, currentY));
    }
    return true;
  }

  function clearRetries() {
    retryTimers.forEach(timer => window.clearTimeout(timer));
    retryTimers.clear();
  }

  function recover(reason = 'unspecified', options = {}) {
    if (!MOBILE_QUERY.matches || pageHasOpenScrollOwner()) return false;

    releaseTransitionOwners();
    requestThemeUnlock(options.forceThemeUnlock === true);

    if (!options.retry) {
      clearRetries();
      [48, 160, 420].forEach(delay => {
        const timer = window.setTimeout(() => {
          retryTimers.delete(timer);
          recover(reason, { retry: true, forceThemeUnlock: options.forceThemeUnlock === true });
        }, delay);
        retryTimers.add(timer);
      });
    }
    return true;
  }

  const onPageShow = event => {
    if (!event.persisted) return;
    window.requestAnimationFrame(() => recover('bfcache-pageshow', { forceThemeUnlock: true }));
  };

  window.addEventListener('pageshow', onPageShow, { capture: true });

  window[RUNTIME_KEY] = {
    recover,
    hasOpenOwner: pageHasOpenScrollOwner,
    destroy() {
      clearRetries();
      window.removeEventListener('pageshow', onPageShow, true);
      delete window[RUNTIME_KEY];
    }
  };
})();
